import { Terminal, TerminalLocation } from '../../../../clients/quipli-service/payment/payment';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { toast } from '../../../../util/toasts';
import { Card } from '../../../../components/card/card';
import { Button } from '../../../../components/form/button';
import { Location } from '../../../../clients/quipli-service/location/location';
import { AddTerminalForm } from '../add-terminal-form';
import { StickyHeader } from '../../../../components/sticky-header/sticky-header';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Typography } from '../../../../components/typography/typography';

export const TerminalList = () => {
  const { getTerminalsForLocations, getTerminalLocations } = usePaymentApiV1();
  const { listLocations } = useAPI();
  const [terminalLocations, setTerminalLocations] = useState<TerminalLocation[]>([]);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [locationList, setLocationList] = useState<Location[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [selectedTerminalId, setSelectedTerminalId] = useState<string | undefined>();

  async function init() {
    try {
      const terminalLocationsData = await getTerminalLocations();
      setTerminalLocations(terminalLocationsData);
      const terminals = await getTerminalsForLocations(
        terminalLocationsData.map((tl) => {
          return tl.id;
        }),
      );
      setTerminals(terminals);
      const locations = await listLocations();
      setLocationList(locations);
    } catch (e) {
      toast('Error displaying terminal list', 'error');
    }
  }

  useEffect(() => {
    init();
  }, []);

  const terminalLocationPage = () => {
    return (
      <div className="px-[10px] bg-gray-100">
        <StickyHeader title={`Card Readers`}>
          <Button className="button-yellow" onClick={() => setModalOpen(true)}>
            Setup Terminal
          </Button>
        </StickyHeader>
        <div className="py-2"></div>
        <div className={'card rounded-2xl bg-white min-w-fit p-2'}>
          <TerminalLocationList
            terminalLocations={terminalLocations}
            locations={locationList}
            terminals={terminals}
            setSelectedTerminalId={setSelectedTerminalId}
            setIsModalOpen={setModalOpen}
          ></TerminalLocationList>
        </div>
      </div>
    );
  };

  return (
    <div className="container-v2">
      {terminalLocationPage()}
      <AddTerminalForm
        isOpen={isModalOpen}
        close={() => {
          setModalOpen(false);
          setSelectedTerminalId(undefined);
          init();
        }}
        terminalId={selectedTerminalId}
      ></AddTerminalForm>
    </div>
  );
};
export const TerminalLocationList = ({
  terminalLocations,
  locations,
  terminals,
  setSelectedTerminalId,
  setIsModalOpen,
}: {
  terminalLocations: TerminalLocation[];
  locations: Location[];
  terminals: Terminal[];
  setSelectedTerminalId: (terminalId: string) => void;
  setIsModalOpen: (isOpen: boolean) => void;
}) => {
  const [expandedTerminalLocation, setExpandedTerminalLocation] = useState<string | undefined>();
  return (
    <div className={'terminal-location-table py-2 pt-8  pb-8 '}>
      {(terminalLocations.length || 0) > 0 && (
        <table className="w-full border-none">
          <thead>
            <tr>
              <th className={'text-left'}>
                <span>Location</span>
              </th>
              <th className="text-left">
                <span>Reader Units</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {terminalLocations.map((loc) => {
              const localTerminals = terminals.filter((t) => {
                return t.terminalLocationId == loc.id;
              });
              return (
                <React.Fragment key={loc.id}>
                  <tr
                    className={classNames('terminal-location-row', {
                      'expanded bg-grey-100 border-none': expandedTerminalLocation === loc.id,
                    })}
                    onClick={() =>
                      setExpandedTerminalLocation((expandedTerminalLocation) =>
                        expandedTerminalLocation === loc.id
                          ? undefined
                          : localTerminals.length > 0
                          ? loc.id
                          : undefined,
                      )
                    }
                  >
                    <td className="text-left flex gap-2 items-center mt-2.5">
                      {locations.find((l) => {
                        return l.id == loc.locationId;
                      })?.name ?? ''}

                      {localTerminals.length > 0 && (
                        <i
                          className={classNames('fa text-[var(--grey-500)]', {
                            'fa-angle-up': expandedTerminalLocation === loc.id,
                            'fa-angle-down': expandedTerminalLocation !== loc.id,
                          })}
                        />
                      )}
                    </td>
                    <td className="text-left">{localTerminals.length}</td>
                  </tr>
                  {loc.id === expandedTerminalLocation && (
                    <tr
                      className={classNames('terminal-list expanded:bg-grey-100 expanded:border-none', {
                        'expanded bg-grey-100 border-none': expandedTerminalLocation === loc.id,
                      })}
                    >
                      <td colSpan={100}>
                        <div className="p-2 pt-0 flex flex-column gap-2">
                          <Card className="w-full">
                            <div className="pb-[2px]">
                              <Typography type={'text-md'} weight={'medium'}>
                                Readers
                              </Typography>
                            </div>
                            <div className={'gap-4  py-2'}>
                              <table className="w-full gap-3">
                                <thead>
                                  <tr>
                                    <th className="text-left">Label</th>
                                    <th className="text-left">Serial</th>
                                    <th className="text-left">Status</th>
                                    <th className="text-left">Model</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {localTerminals.map((lt) => {
                                    return (
                                      <tr key={lt.id}>
                                        <td className="text-left">
                                          {lt.label}
                                          <div className="flex justify-end">
                                            <i
                                              className="fa-solid fa-pencil mr-3 cursor-pointer"
                                              onClick={() => {
                                                setSelectedTerminalId(lt.id);
                                                setIsModalOpen(true);
                                              }}
                                            />
                                          </div>
                                        </td>
                                        <td className="text-left">{lt.serial}</td>
                                        <td className="text-left">{lt.status}</td>
                                        <td className="text-left">{lt.model}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </Card>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
